import React, { useEffect, useState } from 'react';
import { Button, Col, Form, Input, Row, Select, Typography, message } from 'antd';
import { postCall } from '../../../utils/network.util';
import {
    GET_DEFAULT_THEME_COLORS_URL,
    GET_AGENT_COLOUR_THEMES_URL,
    CREATE_THEME_COLORS_URL,
    UPDATE_THEME_COLORS_URL
} from '../../../utils/api-url';
import { dafidyUserCheck } from '../../../utils/constant';
import { isValidArray } from '../../../utils/utilities';
import '../../../styles/colour-config-page.style.scss';

const { Title } = Typography;

const mapThemeToFormValues = (theme) => ({
    themeName: theme.theme_name,
    primaryColor: theme.primary_color,
    secondaryColor: theme.secondary_color,
    defaultButtonBgColor: theme.default_button_bg_color,
    defaultButtonTxtColor: theme.default_button_txt_color,
    headerBgColor: theme.header_bg_color,
    headerTxtColor: theme.header_txt_color,
    dropDownMenuBgColor: theme.drop_down_menu_bg_color,
    dropDownMenuTxtColor: theme.drop_down_menu_txt_color,
    dropDownSelectedBgColor: theme.drop_down_selected_bg_color,
    dropDownSelectedTxtColor: theme.drop_down_selected_txt_color,
    officebookedmale: theme.officebookedmale,
    officebookedfemale: theme.officebookedfemale,
    selfbookedmale: theme.selfbookedmale,
    selfbookedfemale: theme.selfbookedfemale,
    agentbookedmale: theme.agentbookedmale,
    agentbookedfemale: theme.agentbookedfemale,
    otabookedmale: theme.otabookedmale,
    otabookedfemale: theme.otabookedfemale,
    officeblockedmale: theme.officeblockedmale,
    officeblockedfemale: theme.officeblockedfemale,
    selfblockedmale: theme.selfblockedmale,
    selfblockedfemale: theme.selfblockedfemale,
    agentblockedmale: theme.agentblockedmale,
    agentblockedfemale: theme.agentblockedfemale,
    otablockedmale: theme.otablockedmale,
    otablockedfemale: theme.otablockedfemale,
    adminblockedself: theme.adminblockedself,
    adminblockedothers: theme.adminblockedothers,
    phoneblockedgeneral: theme.phoneblockedgeneral,
    selectedseat: theme.selectedseat,
    vacantseat: theme.vacantseat,
    websitebookedmale: theme.websitebookedmale,
    websitebookedfemale: theme.websitebookedfemale
});

export const ThemeColorsPage = () => {
    const isDafidyUser = dafidyUserCheck();
    const [form] = Form.useForm();
    const [themes, setThemes] = useState([]);
    const [selectedThemeId, setSelectedThemeId] = useState(null);
    const [defaultColors, setDefaultColors] = useState({});
    const [loading, setLoading] = useState(false);

    const fetchDefaultColors = async () => {
        try {
            const res = await postCall(GET_DEFAULT_THEME_COLORS_URL, {});
            if (res?.status === 'SUCCESS' && res?.payload?.Status === 'SUCCESS') {
                const defaults = res.payload.Payload || {};
                setDefaultColors(defaults);
                form.setFieldsValue(defaults);
            }
        } catch (e) {
            console.error(e);
        }
    };

    const fetchThemes = async () => {
        setLoading(true);
        try {
            const res = await postCall(GET_AGENT_COLOUR_THEMES_URL, {});
            if (res?.status === 'SUCCESS' && res?.payload?.Status === 'SUCCESS') {
                const list = res.payload.Payload;
                setThemes(isValidArray(list) ? list : []);
            } else {
                setThemes([]);
                message.error(res?.payload?.Message || 'Failed to fetch themes');
            }
        } catch (e) {
            console.error(e);
            setThemes([]);
            message.error('Failed to fetch themes');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDefaultColors();
        fetchThemes();
    }, []);

    const onThemeSelect = (themeId) => {
        if (themeId === undefined || themeId === null) {
            setSelectedThemeId(null);
            form.resetFields();
            form.setFieldsValue(defaultColors);
            return;
        }
        const theme = themes.find(item => item.id === themeId);
        if (theme) {
            setSelectedThemeId(themeId);
            form.setFieldsValue(mapThemeToFormValues(theme));
        }
    };

    const onNewTheme = () => {
        setSelectedThemeId(null);
        form.resetFields();
        form.setFieldsValue(defaultColors);
    };

    const onFinish = async (values) => {
        const { themeName, ...colorData } = values;
        setLoading(true);
        try {
            const url = selectedThemeId ? UPDATE_THEME_COLORS_URL : CREATE_THEME_COLORS_URL;
            const apiPayload = selectedThemeId
                ? { themeId: selectedThemeId, themeName, data: colorData }
                : { themeName, data: colorData };
            const res = await postCall(url, apiPayload);
            if (res?.status === 'SUCCESS' && res?.payload?.Status === 'SUCCESS') {
                message.success(selectedThemeId ? 'Theme updated successfully' : 'Theme created successfully');
                if (!selectedThemeId) {
                    onNewTheme();
                }
                fetchThemes();
            } else {
                message.error(res?.payload?.Message || 'Failed to save theme');
            }
        } catch (e) {
            console.error(e);
            message.error('Failed to save theme');
        } finally {
            setLoading(false);
        }
    };

    if (!isDafidyUser) {
        return null;
    }

    return (
        <div className="colour-config-page">
            <Row justify="center" style={{ paddingTop: 24, marginBottom: 8 }}>
                <Title level={3}>Theme Colors</Title>
            </Row>
            <Row justify="center" align="middle" style={{ marginBottom: 24 }}>
                <span style={{ marginRight: 8, fontWeight: 500 }}>Edit theme :</span>
                <Select
                    style={{ width: 240, marginRight: 16 }}
                    placeholder="Select a theme to edit"
                    allowClear
                    loading={loading}
                    value={selectedThemeId}
                    onChange={onThemeSelect}
                    options={themes.map(theme => ({
                        value: theme.id,
                        label: theme.theme_name
                    }))}
                />
                <Button onClick={onNewTheme}>
                    New Theme
                </Button>
            </Row>
            <Form
                form={form}
                onFinish={onFinish}
                name="themeColors"
                labelCol={{ span: 8 }}
                wrapperCol={{ span: 4 }}
            >
                <Row gutter={16}>
                    <Col span={12}>
                        <Form.Item
                            label="Theme name"
                            name="themeName"
                            rules={[{ required: true, message: 'Please enter the theme name!' }]}
                        >
                            <Input placeholder="Theme name" maxLength={50} />
                        </Form.Item>
                        {[
                            { label: "Primary color", name: "primaryColor" },
                            { label: "Secondary color", name: "secondaryColor" },
                            { label: "Default button bg color", name: "defaultButtonBgColor" },
                            { label: "Default button text color", name: "defaultButtonTxtColor" },
                            { label: "Header bg color", name: "headerBgColor" },
                            { label: "Header text color", name: "headerTxtColor" },
                            { label: "Drop down menu bg color", name: "dropDownMenuBgColor" },
                            { label: "Drop down menu text color", name: "dropDownMenuTxtColor" },
                            { label: "Drop down selected bg color", name: "dropDownSelectedBgColor" },
                            { label: "Drop down selected text color", name: "dropDownSelectedTxtColor" }
                        ].map(({ label, name }) => (
                            <Form.Item
                                key={name}
                                label={label}
                                name={name}
                                rules={[{ required: true, message: `Please select the ${label.toLowerCase()}!` }]}
                            >
                                <Input
                                    type="color"
                                />
                            </Form.Item>
                        ))}
                    </Col>
                    <Col span={12}>
                        {[
                            { label: "Office booked male", name: "officebookedmale" },
                            { label: "Office booked female", name: "officebookedfemale" },
                            { label: "Self booked male", name: "selfbookedmale" },
                            { label: "Self booked female", name: "selfbookedfemale" },
                            { label: "Agent booked male", name: "agentbookedmale" },
                            { label: "Agent booked female", name: "agentbookedfemale" },
                            { label: "OTA booked male", name: "otabookedmale" },
                            { label: "OTA booked female", name: "otabookedfemale" },
                            { label: "Office blocked male", name: "officeblockedmale" },
                            { label: "Office blocked female", name: "officeblockedfemale" },
                            { label: "Self blocked male", name: "selfblockedmale" },
                            { label: "Self blocked female", name: "selfblockedfemale" },
                            { label: "Agent blocked male", name: "agentblockedmale" },
                            { label: "Agent blocked female", name: "agentblockedfemale" },
                            { label: "OTA blocked male", name: "otablockedmale" },
                            { label: "OTA blocked female", name: "otablockedfemale" },
                            { label: "Admin blocked self", name: "adminblockedself" },
                            { label: "Admin blocked others", name: "adminblockedothers" },
                            { label: "Phone blocked general", name: "phoneblockedgeneral" },
                            { label: "Website booked male", name: "websitebookedmale" },
                            { label: "Website booked female", name: "websitebookedfemale" },
                            { label: "Selected seat", name: "selectedseat" },
                            { label: "Vacant seat", name: "vacantseat" }
                        ].map(({ label, name }) => (
                            <Form.Item
                                key={name}
                                label={label}
                                name={name}
                                rules={[{ required: false, message: `Please select the ${label.toLowerCase()}!` }]}
                            >
                                <Input
                                    type="color"
                                />
                            </Form.Item>
                        ))}
                    </Col>
                </Row>

                <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
                    <Button type="primary" htmlType="submit" loading={loading}>
                        {selectedThemeId ? 'Update Theme' : 'Create Theme'}
                    </Button>
                </Form.Item>
            </Form>
        </div>
    );
};
